import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Download, Loader2, Calendar, Filter, FileDown, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStockHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // "all" | "in" | "out"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const [inRes, outRes] = await Promise.all([
        axios.get(`${API}/admin/inventory/stock-in?limit=500`, { withCredentials: true }).catch(() => ({ data: { records: [] } })),
        axios.get(`${API}/admin/inventory/stock-out?limit=500`, { withCredentials: true }).catch(() => ({ data: { records: [] } }))
      ]);

      const inRecords = (inRes.data.records || []).map(r => ({
        ...r,
        type: "IN",
        date: r.received_at || r.created_at,
        flower_name: r.flower_name || r.flower_code,
        qty: r.qty,
        reference: r.stock_in_id || r.supplier
      }));

      const outRecords = (outRes.data.records || []).map(r => ({
        ...r,
        type: "OUT",
        date: r.created_at,
        flower_name: r.flower_name || r.flower_code,
        qty: -r.qty,
        reference: r.reference_id || r.record_id
      }));

      const allRecords = [...inRecords, ...outRecords].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );

      setRecords(allRecords);
    } catch (err) {
      toast.error("Gagal memuat histori stok");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const filteredRecords = records.filter(r => {
    const matchTab = tab === "all" || r.type.toLowerCase() === tab;
    const matchSearch = !search || 
      r.flower_code?.toLowerCase().includes(search.toLowerCase()) ||
      r.flower_name?.toLowerCase().includes(search.toLowerCase());
    const matchStartDate = !startDate || r.date >= startDate;
    const matchEndDate = !endDate || r.date <= endDate + "T23:59:59";
    return matchTab && matchSearch && matchStartDate && matchEndDate;
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate + "T23:59:59");
      params.append("movement_type", tab);

      const res = await axios.get(`${API}/admin/inventory/export/stock-movement?${params.toString()}`, {
        withCredentials: true,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `stock_movement_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV berhasil diexport");
    } catch (err) {
      toast.error("Gagal export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl text-[#1C1A19] mb-2">📊 Stock Movement History</h1>
        <p className="font-body text-sm text-[#8A857D]">Histori pergerakan stok inventory (Stock In & Stock Out)</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          {/* Tabs */}
          <div className="flex gap-1 bg-[#FDF8F6] p-1 rounded-xl">
            <button onClick={() => setTab("all")} data-testid="tab-all"
              className={`px-4 py-2 rounded-lg text-sm font-body transition-colors ${
                tab === "all" ? "bg-white text-seikat-pink shadow-sm" : "text-[#8A857D] hover:text-[#1C1A19]"
              }`}>
              All
            </button>
            <button onClick={() => setTab("in")} data-testid="tab-in"
              className={`px-4 py-2 rounded-lg text-sm font-body transition-colors ${
                tab === "in" ? "bg-white text-seikat-pink shadow-sm" : "text-[#8A857D] hover:text-[#1C1A19]"
              }`}>
              Stock In
            </button>
            <button onClick={() => setTab("out")} data-testid="tab-out"
              className={`px-4 py-2 rounded-lg text-sm font-body transition-colors ${
                tab === "out" ? "bg-white text-seikat-pink shadow-sm" : "text-[#8A857D] hover:text-[#1C1A19]"
              }`}>
              Stock Out
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29A]" size={16} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari bunga..."
              className="w-full pl-9 pr-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50" 
              data-testid="search-input" />
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50"
              data-testid="start-date" />
            <span className="text-[#8A857D] text-sm">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50"
              data-testid="end-date" />
          </div>

          {/* Export Button */}
          <button onClick={handleExport} disabled={exporting} data-testid="export-btn"
            className="flex items-center gap-2 px-4 py-2 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-50">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
        </div>

        {/* Active Filters Summary */}
        {(startDate || endDate || search) && (
          <div className="mt-3 pt-3 border-t border-[#D4859B]/10 flex items-center gap-2 text-xs text-[#8A857D]">
            <Filter size={12} />
            <span>Active filters:</span>
            {startDate && <span className="px-2 py-0.5 bg-[#D4859B]/12 rounded">From: {startDate}</span>}
            {endDate && <span className="px-2 py-0.5 bg-[#D4859B]/12 rounded">To: {endDate}</span>}
            {search && <span className="px-2 py-0.5 bg-[#D4859B]/12 rounded">Search: "{search}"</span>}
            <button onClick={() => { setStartDate(""); setEndDate(""); setSearch(""); }} className="text-seikat-pink hover:underline ml-2">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-seikat-pink" size={28} />
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="text-center py-20">
          <Package size={40} className="mx-auto text-[#D4859B]/40 mb-3" />
          <p className="font-body text-[#A8A29A]">Tidak ada data stock movement</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-history-table">
              <thead>
                <tr className="border-b border-[#D4859B]/10 bg-[#FDF8F6]">
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Date</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Type</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Flower</th>
                  <th className="text-right px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Qty</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Reason/Supplier</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-widest uppercase text-[#A8A29A] font-body">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D4859B]/8">
                {filteredRecords.map((record, idx) => (
                  <tr key={idx} className="hover:bg-[#FDF8F6]/50 transition-colors" data-testid={`record-row-${idx}`}>
                    <td className="px-4 py-3 font-body text-sm text-[#1C1A19]">
                      {new Date(record.date).toLocaleDateString("id-ID", { year: "numeric", month: "short", day: "numeric" })}
                      <br />
                      <span className="text-xs text-[#A8A29A]">{new Date(record.date).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-body text-xs px-2.5 py-1 rounded-full ${
                        record.type === "IN" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-red-100 text-red-700"
                      }`}>
                        {record.type === "IN" ? "↓ IN" : "↑ OUT"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-body text-sm font-medium text-[#1C1A19]">{record.flower_name}</p>
                      <p className="font-body text-xs text-[#A8A29A]">{record.flower_code}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-body text-sm font-semibold ${
                        record.type === "IN" ? "text-green-600" : "text-red-600"
                      }`}>
                        {record.qty > 0 ? "+" : ""}{record.qty}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-body text-sm text-[#8A857D]">
                      {record.type === "IN" ? (record.supplier || "-") : (record.reason || "-")}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-[#A8A29A] truncate max-w-[200px]">
                      {record.reference || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Summary */}
          <div className="border-t border-[#D4859B]/10 bg-[#FDF8F6] px-4 py-3 flex items-center justify-between">
            <span className="font-body text-xs text-[#8A857D]">
              Showing {filteredRecords.length} of {records.length} records
            </span>
            <span className="font-body text-xs text-[#8A857D]">
              In: {filteredRecords.filter(r => r.type === "IN").length} | 
              Out: {filteredRecords.filter(r => r.type === "OUT").length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
